/**
 * A login lockout held across auth screens.
 *
 * The wait is stored as an absolute deadline rather than a duration: the
 * password screen unmounts whenever the user visits forgot-password, and a
 * duration would restart the countdown at its full length on the way back.
 */
export interface LoginLockout {
  code: string;
  /** Epoch ms at which the next attempt is allowed. */
  retryAt: number;
  /** The account the wait belongs to; a different email must not inherit it. */
  email: string;
}

export interface ActiveLockout {
  code: string;
  retryAfterSeconds: number;
}

/** Builds the stored form from a lockout envelope the server just returned. */
export function lockoutFromResponse(
  code: string,
  retryAfterSeconds: number,
  email: string,
  now: number = Date.now(),
): LoginLockout {
  return { code, retryAt: now + retryAfterSeconds * 1000, email };
}

/**
 * The wait still owed on `email`, or null if there is none, it has run out, or
 * it belongs to another account.
 */
export function activeLockout(
  lockout: LoginLockout | null,
  email: string,
  now: number = Date.now(),
): ActiveLockout | null {
  if (!lockout || lockout.email !== email) return null;

  const retryAfterSeconds = Math.ceil((lockout.retryAt - now) / 1000);
  if (retryAfterSeconds <= 0) return null;

  return { code: lockout.code, retryAfterSeconds };
}
